import { DOCUMENT, Injectable, InjectionToken, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { firstValueFrom, timeout } from 'rxjs';

import { apps, guid } from '../constants';
import type { App } from '../models';
import { AppProperty, CompanyProperty, UserProperty } from '../properties';
// Import diretto per evitare il ciclo shared -> services -> navigation -> shared.
import { AppStateHolder, buildAppDeepLink } from '../shared/app-state.holder';
import { AppService } from './app.service';
import { NotificationContentService } from './notification-content.service';
import type { NotificationAction, NotificationItem } from './notifications';

const PENDING_KEY = 'amsuite.notification-navigation';
const PENDING_TTL_MS = 5 * 60_000;
const REQUEST_TIMEOUT_MS = 15_000;

interface AccessibleCompany {
  readonly id: string;
  readonly apps: readonly App[];
}

interface Destination {
  readonly appId: string;
  readonly action: NotificationAction;
  readonly detailUnavailable?: boolean;
}

interface PendingNavigation {
  readonly version: 1;
  readonly accountId: string;
  readonly expiresAt: number;
  readonly companyId: string;
  readonly destination: Destination;
}

/** Browser boundary injectable so the reload continuation can be tested without leaving the page. */
export interface NotificationNavigationBrowser {
  read(): string | null;
  save(value: string): void;
  clear(): void;
  reload(): void;
}

export const NOTIFICATION_NAVIGATION_BROWSER = new InjectionToken<NotificationNavigationBrowser>(
  'NOTIFICATION_NAVIGATION_BROWSER',
  {
    providedIn: 'root',
    factory: () => {
      const window = inject(DOCUMENT).defaultView;
      return {
        read: () => window?.sessionStorage.getItem(PENDING_KEY) ?? null,
        save: value => {
          if (!window) throw new Error('Browser unavailable');
          window.sessionStorage.setItem(PENDING_KEY, value);
        },
        clear: () => window?.sessionStorage.removeItem(PENDING_KEY),
        reload: () => window?.location.assign('/dashboard'),
      };
    },
  },
);

/** Resolves origin, content and company independently; notification metadata never grants access. */
@Injectable({ providedIn: 'root' })
export class NotificationNavigationService {
  private readonly user = inject(UserProperty);
  private readonly company = inject(CompanyProperty);
  private readonly app = inject(AppProperty);
  private readonly appService = inject(AppService);
  private readonly content = inject(NotificationContentService);
  private readonly msal = inject(MsalService);
  private readonly router = inject(Router);
  private readonly state = inject(AppStateHolder);
  private readonly messages = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly browser = inject(NOTIFICATION_NAVIGATION_BROWSER);
  private generation = 0;
  private opening = false;

  /** True means that navigation or its company-change continuation was accepted. */
  async open(item: NotificationItem): Promise<boolean> {
    if (this.opening) return false;
    if (this.isImpersonating()) return this.reject('impersonation');
    const accountId = this.accountId();
    if (!accountId) return this.reject('unavailable');

    const destination = this.destination(item);
    if (!destination) return this.reject('invalid');
    const generation = ++this.generation;
    const initialCompanyId = this.selectedCompanyId();
    this.removePending();
    this.opening = true;

    try {
      const candidates = this.companies().filter(company => this.findApp(company.apps, destination.appId));
      if (candidates.length === 0) return this.reject('application-unavailable');

      const targetCompany = await this.resolveCompany(item, candidates, () =>
        this.isCurrent(accountId, generation) && this.selectedCompanyId() === initialCompanyId);
      if (!this.isCurrent(accountId, generation) || this.selectedCompanyId() !== initialCompanyId) return false;
      if (!targetCompany) return this.reject('company-unavailable');

      if (targetCompany.id !== initialCompanyId) {
        // Solo dati di routing, senza titolo/corpo e senza URL forniti dalla notifica.
        this.browser.save(JSON.stringify({
          version: 1,
          accountId,
          expiresAt: Date.now() + PENDING_TTL_MS,
          companyId: targetCompany.id,
          destination,
        } satisfies PendingNavigation));
        await this.appService.setDataOwner(targetCompany.id);
        if (!this.isCurrent(accountId, generation)) {
          this.removePending();
          return false;
        }
        this.user.dataOwnerId = targetCompany.id;
        this.browser.reload();
        return true;
      }

      return await this.navigate(destination, accountId, generation);
    } catch (error: unknown) {
      this.removePending();
      return this.isCurrent(accountId, generation) ? this.reject(this.errorKey(error)) : false;
    } finally {
      this.opening = false;
    }
  }

  /**
   * Called after the shell has loaded and selected its company. True consumes a pending
   * attempt (including an explicit error), preventing onSelectCompany from resetting it.
   */
  async resumePending(): Promise<boolean> {
    let raw: string | null;
    try {
      raw = this.browser.read();
    } catch {
      return false;
    }
    if (!raw) return false;
    this.removePending();

    let pending: PendingNavigation;
    try {
      const value: unknown = JSON.parse(raw);
      if (!this.isPending(value)) return this.rejectAndConsume('invalid');
      pending = value;
    } catch {
      return this.rejectAndConsume('invalid');
    }
    if (pending.accountId !== this.accountId()) return false;
    if (pending.expiresAt <= Date.now()) return this.rejectAndConsume('expired');
    if (this.isImpersonating()) return this.rejectAndConsume('impersonation');

    const selected = this.companies().find(company => company.id === this.selectedCompanyId());
    if (!selected || selected.id !== pending.companyId) return this.rejectAndConsume('company-unavailable');
    if (!this.findApp(selected.apps, pending.destination.appId)) return this.rejectAndConsume('application-unavailable');

    const generation = ++this.generation;
    try {
      await this.navigate(pending.destination, pending.accountId, generation);
    } catch (error: unknown) {
      if (this.isCurrent(pending.accountId, generation)) this.reject(this.errorKey(error));
    }
    return true;
  }

  /** Logout/account change also invalidates a lookup or content request still in flight. */
  clearPending(): void {
    this.generation++;
    this.removePending();
  }

  private async navigate(destination: Destination, accountId: string, generation: number): Promise<boolean> {
    const selectedCompanyId = this.selectedCompanyId();
    const app = this.findApp(this.currentApps(), destination.appId);
    if (!app) return this.reject('application-unavailable');
    const url = this.appUrl(app.url, destination.action);
    if (!url) return this.reject('invalid');

    const resource = await firstValueFrom(this.content.getTarget(destination.action).pipe(timeout(REQUEST_TIMEOUT_MS)));
    if (!this.isCurrent(accountId, generation) || selectedCompanyId !== this.selectedCompanyId()) return false;
    if (resource == null || resource === false) return this.reject('unavailable');
    this.state.setAppNavbarClass(destination.appId);
    const navigated = await this.router.navigate(['/app', url]);
    if (!navigated) return this.reject('failed');
    if (destination.detailUnavailable) this.feedback('detail-unavailable', 'info');
    return true;
  }

  private async resolveCompany(
    item: NotificationItem,
    candidates: readonly AccessibleCompany[],
    isCurrent: () => boolean,
  ): Promise<AccessibleCompany | undefined> {
    const current = candidates.find(company => company.id === this.selectedCompanyId());
    const ownerId = this.id(item.dataOwnerId);

    // La corrispondenza Dialog e i progetti senza tenant sono personali. Cambiare azienda
    // serve solo se l'app non è nel catalogo corrente, non per inseguire DataOwnerId.
    if (item.action.kind === 'dialog' || item.action.kind === 'project'
      || item.action.kind === 'application' || item.source === 'legacy') {
      return current ?? candidates[0];
    }

    if (item.action.kind === 'news') {
      // Comunica persiste il party owner del produttore, che può essere selezionabile.
      const direct = ownerId && candidates.find(company => company.id === ownerId);
      if (direct) return direct;
      // Le news condivise possono appartenere al suite owner, non al party owner corrente.
      const suiteOwnerId = this.id(item.metadata['SuiteOwnerId'] ?? item.metadata['suiteOwnerId']);
      if (!suiteOwnerId) return undefined;
      return this.findCompanyBySuiteOwner(suiteOwnerId, candidates, current, isCurrent);
    }

    // Workflow attività persiste il SUITE owner. Anche un id uguale a quello di una
    // società va verificato dal lookup, non reinterpretato come party owner.
    if (!ownerId) return undefined;
    return this.findCompanyBySuiteOwner(ownerId, candidates, current, isCurrent);
  }

  private async findCompanyBySuiteOwner(
    suiteOwnerId: string,
    candidates: readonly AccessibleCompany[],
    current: AccessibleCompany | undefined,
    isCurrent: () => boolean,
  ): Promise<AccessibleCompany | undefined> {
    const ordered = current ? [current, ...candidates.filter(company => company !== current)] : candidates;
    for (const company of ordered) {
      if (!isCurrent()) return undefined;
      try {
        const actualOwner = await firstValueFrom(this.appService.getPartySuiteOwner(company.id).pipe(timeout(REQUEST_TIMEOUT_MS)));
        if (this.id(actualOwner) === suiteOwnerId) return company;
      } catch {
        // Una società non risolvibile non impedisce di usare un'altra società autorizzata.
      }
    }
    return undefined;
  }

  private destination(item: NotificationItem): Destination | null {
    if (!this.validAction(item.action)) return null;
    const appId = item.action.kind === 'dialog' ? apps.dialog
      : item.action.kind === 'activity' || item.action.kind === 'project' ? apps.calendar
        : item.action.kind === 'news' ? apps.comunica : this.id(item.appId);
    return appId ? {
      appId: appId.toLowerCase(), action: item.action,
      detailUnavailable: this.missingKnownDetail(item),
    } : null;
  }

  private missingKnownDetail(item: NotificationItem): boolean {
    if (item.source !== 'notifier' || item.action.kind !== 'application') return false;
    const type = item.metadata['type'] ?? item.metadata['Type'];
    return ['Workflow', 'WorkflowProject', 'RequestCreated', 'MessageAdded', 'RequestStateChanged'].includes(String(type))
      || item.metadata['NewsId'] !== undefined || item.metadata['newsId'] !== undefined
      || item.metadata['CategoryId'] !== undefined || item.metadata['categoryId'] !== undefined
      || [apps.dialog, apps.calendar, apps.comunica].some(appId => this.id(appId) === this.id(item.appId));
  }

  private appUrl(url: string, action: NotificationAction): string | null {
    try {
      const parsed = new URL(url);
      if (!['https:', 'http:'].includes(parsed.protocol) || parsed.username || parsed.password) return null;
    } catch {
      return null;
    }
    switch (action.kind) {
      case 'dialog': return buildAppDeepLink(url, 'dialog', action.requestId);
      case 'activity': return buildAppDeepLink(url, 'activity', action.objectId);
      case 'project': return buildAppDeepLink(url, 'project', action.objectId);
      case 'news': return buildAppDeepLink(url, `category/${action.categoryId}/news`, action.newsId);
      case 'application': return url;
    }
  }

  private companies(): readonly AccessibleCompany[] {
    const companies: unknown = this.user.userCompaniesList;
    if (!Array.isArray(companies)) return [];
    return companies.filter((company): company is AccessibleCompany => this.isRecord(company)
      && this.id(company['id']) !== undefined && Array.isArray(company['apps']))
      .map(company => ({ id: company.id.toLowerCase(), apps: company.apps }));
  }

  private currentApps(): readonly App[] {
    const apps: unknown = this.company.userCompanyApps;
    return Array.isArray(apps) ? apps : [];
  }

  private findApp(apps: readonly App[], appId: string): App | undefined {
    return apps.find(app => this.id(app?.id) === appId.toLowerCase());
  }

  private selectedCompanyId(): string | undefined {
    return this.id(this.user.userSelectedCompany?.id);
  }

  private accountId(): string | null {
    const instance = this.msal.instance;
    const active = instance.getActiveAccount?.();
    const accounts = instance.getAllAccounts();
    return active?.homeAccountId ?? (accounts.length === 1 ? accounts[0].homeAccountId : null) ?? null;
  }

  private isImpersonating(): boolean {
    // Il cookie può essere già presente prima del completamento del bootstrap della shell.
    return this.app.impersonateIsActive === true || this.app.impersonateIsActive === 'true'
      || this.state.getCookie('ImpersonateState') === 'true'
      || !!this.state.getCookie('ImpersonationSessionId');
  }

  private isCurrent(accountId: string, generation: number): boolean {
    return this.generation === generation && this.accountId() === accountId && !this.isImpersonating();
  }

  private isPending(value: unknown): value is PendingNavigation {
    if (!this.isRecord(value) || !this.isRecord(value['destination'])) return false;
    const destination = value['destination'];
    return value['version'] === 1 && typeof value['accountId'] === 'string'
      && typeof value['expiresAt'] === 'number' && Number.isFinite(value['expiresAt'])
      && value['expiresAt'] <= Date.now() + PENDING_TTL_MS
      && this.id(value['companyId']) !== undefined && this.id(destination['appId']) !== undefined
      && (destination['detailUnavailable'] === undefined || typeof destination['detailUnavailable'] === 'boolean')
      && this.validAction(destination['action'])
      && this.matchesTargetApp(destination['appId'], destination['action']);
  }

  private matchesTargetApp(appId: unknown, action: NotificationAction): boolean {
    const expected = action.kind === 'dialog' ? apps.dialog
      : action.kind === 'activity' || action.kind === 'project' ? apps.calendar
        : action.kind === 'news' ? apps.comunica : appId;
    return this.id(appId) === this.id(expected);
  }

  private validAction(value: unknown): value is NotificationAction {
    if (!this.isRecord(value)) return false;
    switch (value['kind']) {
      case 'dialog': return this.validSegment(value['requestId']);
      case 'activity':
      case 'project': return this.validSegment(value['objectId']);
      case 'news': return this.validSegment(value['categoryId']) && this.validSegment(value['newsId']);
      case 'application': return true;
      default: return false;
    }
  }

  private validSegment(value: unknown): value is string {
    return typeof value === 'string' && /^[a-z\d_-]{1,128}$/i.test(value) && this.id(value) !== undefined;
  }

  private id(value: unknown): string | undefined {
    if (typeof value !== 'string' || !value.trim() || value.length > 256) return undefined;
    const normalized = value.trim().toLowerCase();
    return normalized === guid.empty.toLowerCase() ? undefined : normalized;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private errorKey(error: unknown): string {
    const status = this.isRecord(error) ? error['status'] : undefined;
    return status === 403 || status === 404 ? 'unavailable' : 'failed';
  }

  private reject(key: string): false {
    this.feedback(key, 'warn');
    return false;
  }

  private feedback(key: string, severity: 'warn' | 'info'): void {
    this.messages.add({
      severity,
      summary: this.translate.instant('notifications.navigation.title'),
      detail: this.translate.instant(`notifications.navigation.${key}`),
    });
  }

  private rejectAndConsume(key: string): true {
    this.reject(key);
    return true;
  }

  private removePending(): void {
    try {
      this.browser.clear();
    } catch {
      // Storage may be unavailable; ordinary navigation still works without a company switch.
    }
  }
}
